import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CareersSection = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const openPositions = [
    {
      title: 'Senior Full-Stack Engineer',
      department: 'Engineering',
      location: 'Belgrade / Remote',
      type: 'Full-time',
      description: 'Build scalable features for our AI-powered onboarding platform'
    },
    {
      title: 'Product Designer (UX/UI)',
      department: 'Product',
      location: 'Berlin / Remote',
      type: 'Full-time',
      description: 'Craft intuitive experiences for enterprise HR teams'
    },
    {
      title: 'Enterprise Account Executive',
      department: 'Sales',
      location: 'London / Remote',
      type: 'Full-time',
      description: 'Drive growth by selling to Fortune 500 HR leaders'
    },
    {
      title: 'Customer Success Manager',
      department: 'Customer Success',
      location: 'NYC / Remote',
      type: 'Full-time',
      description: 'Ensure enterprise customers achieve onboarding excellence'
    },
    {
      title: 'ML Engineer (AI Buddy)',
      department: 'Engineering',
      location: 'Remote (EU/US)',
      type: 'Full-time',
      description: 'Develop NLP models for our AI-powered HR assistant'
    },
    {
      title: 'Content Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create thought leadership content for HR tech audience'
    },
  ];

  const benefits = [
    {
      icon: 'DollarSign',
      title: 'Competitive Salary',
      description: 'Market-leading compensation + equity'
    },
    {
      icon: 'Home',
      title: 'Remote-First',
      description: 'Work from anywhere in 85+ countries'
    },
    {
      icon: 'Plane',
      title: 'Unlimited PTO',
      description: 'Minimum 3 weeks vacation policy'
    },
    {
      icon: 'Heart',
      title: 'Health Coverage',
      description: 'Premium medical, dental, vision'
    },
    {
      icon: 'Zap',
      title: 'Learning Budget',
      description: '€2,000/year for courses & conferences'
    },
    {
      icon: 'Users',
      title: 'Team Offsites',
      description: 'Bi-annual global team gatherings'
    },
  ];

  const departments = ['All', 'Engineering', 'Product', 'Sales', 'Customer Success', 'Marketing'];

  const filteredPositions = selectedDepartment === 'All' 
    ? openPositions 
    : openPositions?.filter(pos => pos?.department === selectedDepartment);

  return (
    <section id="careers" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <Icon name="Briefcase" size={32} className="text-primary" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">Careers at First On Date</h2>
            <p className="text-muted-foreground mt-1">Join our global team building the future of employee onboarding</p>
          </div>
        </div>

        {/* Culture Pitch */}
        <div className="prose prose-invert max-w-none mb-12">
          <p className="text-lg text-muted-foreground leading-relaxed">
            We're a remote-first company with team members across 85 countries. We value autonomy, transparency, and impact. Our culture combines startup energy with enterprise maturity—ship fast, think long-term, and always put users first.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {benefits?.map((benefit, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Icon name={benefit?.icon} size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{benefit?.title}</h3>
              <p className="text-sm text-muted-foreground">{benefit?.description}</p>
            </div>
          ))}
        </div>

        {/* Open Positions */}
        <div>
          <h3 className="text-2xl font-bold text-foreground mb-6">Open Positions ({filteredPositions?.length})</h3>
          
          {/* Department Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {departments?.map((dept) => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedDepartment === dept
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background/50 text-muted-foreground hover:bg-card hover:text-foreground border border-border'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="space-y-4">
            {filteredPositions?.map((position, index) => (
              <div 
                key={index}
                className="bg-background/50 border border-border rounded-lg p-6 hover:border-primary/50 hover:shadow-elevation-2 transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-foreground mb-2">{position?.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3">{position?.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-semibold rounded-full flex items-center gap-1">
                        <Icon name="Briefcase" size={12} />
                        {position?.department}
                      </span>
                      <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-semibold rounded-full flex items-center gap-1">
                        <Icon name="MapPin" size={12} />
                        {position?.location}
                      </span>
                      <span className="px-3 py-1 bg-success/20 text-success text-xs font-semibold rounded-full flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {position?.type}
                      </span>
                    </div>
                  </div>
                  <Button 
                    variant="default"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => window.open(`/careers/apply?position=${encodeURIComponent(position?.title)}`, '_blank')}
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Application Process */}
        <div className="mt-12 p-6 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-4">
            <Icon name="Rocket" size={24} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Our Hiring Process</h3>
              <p className="text-sm text-muted-foreground mb-4">
                1️⃣ Application Review (3 days) → 2️⃣ Recruiter Screen (30 min) → 3️⃣ Technical/Role-Fit Interview (60 min) → 4️⃣ Team Interviews (2-3 sessions) → 5️⃣ Offer Decision (48 hours)
              </p>
              <p className="text-sm text-muted-foreground">
                Average time to hire: 18 days. We value transparency—expect regular updates at every stage.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4">
          <Button 
            variant="default"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.open('mailto:careers@firstondate.com', '_blank')}
          >
            General Inquiries
          </Button>
          <Button 
            variant="outline"
            iconName="Users"
            iconPosition="left"
            onClick={() => window.open('/culture-deck.pdf', '_blank')}
          >
            Culture Deck
          </Button>
          <Button 
            variant="ghost"
            iconName="Calendar"
            iconPosition="left"
            onClick={() => window.open('/careers/meet-the-team', '_blank')}
          >
            Meet the Team
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CareersSection;